import React, { useMemo } from 'react';
import StatCell from '../components/StatCell';
import { euro, pct, monthName } from '../format';

const VIEW_WIDTH = 1000;
const VIEW_HEIGHT = 300;
const PADDING = 8;

/**
 * Build the polyline points for the net-worth series.
 *
 * A flat series (every snapshot identical) would divide by zero on the vertical
 * scale, so it is drawn along the middle instead.
 *
 * @param {Array<{date: string, value: number}>} series
 * @returns {string} SVG `points` attribute
 */
function buildPoints(series) {
  if (series.length < 2) {
    return '';
  }

  const values = series.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;

  const usableWidth = VIEW_WIDTH - PADDING * 2;
  const usableHeight = VIEW_HEIGHT - PADDING * 2;

  return series
    .map((point, i) => {
      const x = PADDING + (i / (series.length - 1)) * usableWidth;
      const y = span === 0
        ? PADDING + usableHeight / 2
        : PADDING + usableHeight - ((point.value - min) / span) * usableHeight;

      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

/**
 * Chapter 03 — Trend.
 *
 * A single flat polyline across every snapshot (downsampled server-side), with
 * the four all-time stats beneath it. No area fill, no gradient — the handoff is
 * explicit that the line stands alone.
 *
 * `stroke-dasharray` / `stroke-dashoffset` are set to the path length so the
 * owner's draw-on animation has a seam to hook into; both default to a fully
 * drawn line.
 */
function Trend({ story }) {
  // Memoised so the `|| []` fallback doesn't hand useMemo a new array identity
  // on every render and redraw the polyline for nothing.
  const series = useMemo(() => story?.snapshots || [], [story?.snapshots]);
  const points = useMemo(() => buildPoints(series), [series]);
  const hasLine = points.length > 0;

  return (
    <>
      <h2 className="story-h2">
        The long line only goes one way when you keep showing up.
      </h2>

      <div className="story-trend">
        <div className="story-trend-chart">
          {hasLine ? (
            <svg
              viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
              preserveAspectRatio="none"
              role="img"
              aria-label={`Net worth from ${series[0].date} to ${series[series.length - 1].date}`}
            >
              <polyline className="story-trend-line" points={points} data-story-trend-line />
            </svg>
          ) : (
            <p className="story-empty-text" style={{ padding: 32 }}>
              Not enough history yet — the line starts drawing itself once you have a
              second snapshot.
            </p>
          )}
        </div>

        <div className="story-stat-row">
          <StatCell label="First snapshot" value={euro(story?.firstSnapshot?.value)} />
          <StatCell label="Snapshots taken" value={story?.snapshotCount ?? '---'} />
          {/* The design gives this stat the bare label "BEST MONTH"; the month
              itself rides on the value as a title so the row keeps its rhythm. */}
          <StatCell
            label="Best month"
            value={
              <span title={story?.bestMonth ? monthName(story.bestMonth.month) : undefined}>
                {pct(story?.bestMonth?.pct)}
              </span>
            }
          />
          <StatCell label="Today" value={euro(story?.netWorth)} accent />
        </div>
      </div>
    </>
  );
}

export default Trend;
